/**
 * CR096: Publish Policy Tab - Configure publish gates and warnings
 */

import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ShieldAlert, ShieldCheck, Plus, Edit2, Trash2, AlertTriangle, Info } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface PublishRule {
  rule_id: string;
  rule_name: string;
  is_enabled: boolean;
  severity: "GATE" | "WARN";
  applies_to: "ALL" | "DEAL_TYPES";
  deal_types: string[] | null;
  rule_type: string;
  params_json: Record<string, unknown>;
  updated_by: string | null;
  updated_at: string;
}

interface PublishPolicyTabProps {
  rules: PublishRule[];
  loading: boolean;
  onRefresh: () => void;
  onUpdate: (rule: PublishRule) => void;
  onAdd: (rule: PublishRule) => void;
}

const RULE_TYPES = [
  { value: "REQUIRE_PRESENT", label: "Require Present", description: "Field must exist and not be empty" },
  { value: "MIN_NUMBER", label: "Minimum Number", description: "Numeric field must be >= threshold" },
  { value: "REQUIRE_ENRICHMENT_STATUS", label: "Require Enrichment Status", description: "Status must be in allowed list" },
  { value: "REQUIRE_DATE_WINDOW", label: "Require Date Window", description: "Start and end dates must be valid" },
];

export function PublishPolicyTab({ rules, loading, onRefresh, onUpdate, onAdd }: PublishPolicyTabProps) {
  const [editingRule, setEditingRule] = useState<PublishRule | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state for editing
  const [formData, setFormData] = useState({
    rule_id: "",
    rule_name: "",
    severity: "GATE" as "GATE" | "WARN",
    applies_to: "ALL" as "ALL" | "DEAL_TYPES",
    deal_types: "",
    rule_type: "REQUIRE_PRESENT",
    attribute_key: "",
    min_value: "",
    allowed_values: "",
    start_key: "",
    end_key: "",
  });

  const openEditDialog = (rule: PublishRule) => {
    const params = rule.params_json as Record<string, unknown>;
    setFormData({
      rule_id: rule.rule_id,
      rule_name: rule.rule_name,
      severity: rule.severity,
      applies_to: rule.applies_to,
      deal_types: rule.deal_types?.join(", ") || "",
      rule_type: rule.rule_type,
      attribute_key: (params.attribute_key as string) || "",
      min_value: params.min !== undefined ? String(params.min) : "",
      allowed_values: Array.isArray(params.allowed) ? params.allowed.join(", ") : "",
      start_key: (params.start_key as string) || "",
      end_key: (params.end_key as string) || "",
    });
    setEditingRule(rule);
    setIsCreating(false);
  };

  const openCreateDialog = () => {
    const newId = `rule_${Date.now()}`;
    setFormData({
      rule_id: newId,
      rule_name: "",
      severity: "GATE",
      applies_to: "ALL",
      deal_types: "",
      rule_type: "REQUIRE_PRESENT",
      attribute_key: "merchant_name",
      min_value: "",
      allowed_values: "",
      start_key: "",
      end_key: "",
    });
    setEditingRule({
      rule_id: newId,
      rule_name: "",
      is_enabled: true,
      severity: "GATE",
      applies_to: "ALL",
      deal_types: null,
      rule_type: "REQUIRE_PRESENT",
      params_json: { attribute_key: "merchant_name" },
      updated_by: null,
      updated_at: new Date().toISOString(),
    });
    setIsCreating(true);
  };

  const closeDialog = () => {
    setEditingRule(null);
    setIsCreating(false);
  };

  const buildParamsJson = (): Record<string, unknown> => {
    switch (formData.rule_type) {
      case "REQUIRE_PRESENT":
        return { attribute_key: formData.attribute_key };
      case "MIN_NUMBER":
        return { attribute_key: formData.attribute_key, min: parseFloat(formData.min_value) || 0 };
      case "REQUIRE_ENRICHMENT_STATUS":
        return {
          attribute_key: formData.attribute_key,
          allowed: formData.allowed_values.split(",").map((s) => s.trim()).filter(Boolean),
        };
      case "REQUIRE_DATE_WINDOW":
        return { start_key: formData.start_key, end_key: formData.end_key };
      default:
        return {};
    }
  };

  const handleSave = async () => {
    if (!formData.rule_name.trim()) {
      toast.error("Rule name is required");
      return;
    }

    setSaving(true);
    try {
      const params_json = buildParamsJson();
      const deal_types = formData.applies_to === "DEAL_TYPES" && formData.deal_types
        ? formData.deal_types.split(",").map((s) => s.trim()).filter(Boolean)
        : null;

      if (isCreating) {
        const { data, error } = await supabase.functions.invoke("command-center-attributes", {
          method: "POST",
          body: {
            rule_id: formData.rule_id,
            rule_name: formData.rule_name,
            severity: formData.severity,
            applies_to: formData.applies_to,
            deal_types,
            rule_type: formData.rule_type,
            params_json,
          },
        });

        if (error) throw error;
        if (data.rule) onAdd(data.rule);
        toast.success("Rule created");
      } else {
        const { data, error } = await supabase.functions.invoke("command-center-attributes", {
          method: "PUT",
          body: {
            rule_id: formData.rule_id,
            rule_name: formData.rule_name,
            severity: formData.severity,
            applies_to: formData.applies_to,
            deal_types,
            rule_type: formData.rule_type,
            params_json,
          },
        });

        if (error) throw error;
        if (data.rule) onUpdate(data.rule);
        toast.success("Rule updated");
      }

      closeDialog();
    } catch (err) {
      console.error("[PublishPolicyTab] Save error:", err);
      toast.error("Failed to save rule");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (rule: PublishRule) => {
    try {
      const { data, error } = await supabase.functions.invoke("command-center-attributes", {
        method: "PUT",
        body: {
          rule_id: rule.rule_id,
          is_enabled: !rule.is_enabled,
        },
      });

      if (error) throw error;
      if (data.rule) onUpdate(data.rule);
      toast.success(`Rule ${data.rule.is_enabled ? "enabled" : "disabled"}`);
    } catch (err) {
      console.error("[PublishPolicyTab] Toggle error:", err);
      toast.error("Failed to toggle rule");
    }
  };

  const handleDelete = async (ruleId: string) => {
    try {
      const { error } = await supabase.functions.invoke("command-center-attributes", {
        method: "DELETE",
        body: { rule_id: ruleId },
      });

      if (error) throw error;
      onRefresh();
      toast.success("Rule disabled");
    } catch (err) {
      console.error("[PublishPolicyTab] Delete error:", err);
      toast.error("Failed to disable rule");
    }
  };

  const enabledGates = rules.filter((r) => r.is_enabled && r.severity === "GATE").length;
  const enabledWarns = rules.filter((r) => r.is_enabled && r.severity === "WARN").length;

  return (
    <div className="space-y-4">
      {/* Info Banner */}
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          <strong>Gates</strong> block publishing if a deal fails the check.
          <strong className="ml-2">Warnings</strong> allow publishing but flag issues for review.
        </AlertDescription>
      </Alert>

      {/* Stats */}
      <div className="flex gap-4">
        <Badge variant="destructive" className="px-3 py-1">
          <ShieldAlert className="h-3 w-3 mr-1" />
          {enabledGates} Active Gate{enabledGates !== 1 ? "s" : ""}
        </Badge>
        <Badge variant="secondary" className="px-3 py-1">
          <AlertTriangle className="h-3 w-3 mr-1" />
          {enabledWarns} Active Warning{enabledWarns !== 1 ? "s" : ""}
        </Badge>
      </div>

      {/* Rules Card */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5" />
              Publish Policy Rules
            </CardTitle>
            <CardDescription>
              Define rules that must pass before deals can be published to production.
            </CardDescription>
          </div>
          <Button onClick={openCreateDialog} size="sm">
            <Plus className="h-4 w-4 mr-1" />
            Add Rule
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]">Enabled</TableHead>
                  <TableHead>Rule Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Severity</TableHead>
                  <TableHead>Applies To</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No publish rules configured
                    </TableCell>
                  </TableRow>
                ) : (
                  rules.map((rule) => (
                    <TableRow key={rule.rule_id} className={!rule.is_enabled ? "opacity-50" : ""}>
                      <TableCell>
                        <Switch
                          checked={rule.is_enabled}
                          onCheckedChange={() => handleToggleEnabled(rule)}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{rule.rule_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-mono text-xs">
                          {rule.rule_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={rule.severity === "GATE" ? "destructive" : "secondary"}>
                          {rule.severity}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {rule.applies_to === "ALL" ? (
                          <span className="text-muted-foreground">All deals</span>
                        ) : (
                          <span className="text-xs">
                            {rule.deal_types?.join(", ") || "None"}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(rule)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(rule.rule_id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit/Create Dialog */}
      <Dialog open={!!editingRule} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{isCreating ? "Create Publish Rule" : "Edit Publish Rule"}</DialogTitle>
            <DialogDescription>
              Configure the rule parameters. Gates block publishing, warnings allow it with a flag.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="rule_name">Rule Name</Label>
              <Input
                id="rule_name"
                value={formData.rule_name}
                onChange={(e) => setFormData({ ...formData, rule_name: e.target.value })}
                placeholder="e.g., Require Merchant Name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Severity</Label>
                <Select
                  value={formData.severity}
                  onValueChange={(v) => setFormData({ ...formData, severity: v as "GATE" | "WARN" })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="GATE">Gate (Blocks)</SelectItem>
                    <SelectItem value="WARN">Warning (Flags)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Applies To</Label>
                <Select
                  value={formData.applies_to}
                  onValueChange={(v) => setFormData({ ...formData, applies_to: v as "ALL" | "DEAL_TYPES" })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">All Deals</SelectItem>
                    <SelectItem value="DEAL_TYPES">Specific Deal Types</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {formData.applies_to === "DEAL_TYPES" && (
              <div className="space-y-2">
                <Label htmlFor="deal_types">Deal Types (comma-separated)</Label>
                <Input
                  id="deal_types"
                  value={formData.deal_types}
                  onChange={(e) => setFormData({ ...formData, deal_types: e.target.value })}
                  placeholder="e.g., BONUS, DISCOUNT"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label>Rule Type</Label>
              <Select
                value={formData.rule_type}
                onValueChange={(v) => setFormData({ ...formData, rule_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RULE_TYPES.map((rt) => (
                    <SelectItem key={rt.value} value={rt.value}>
                      {rt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {RULE_TYPES.find((rt) => rt.value === formData.rule_type)?.description}
              </p>
            </div>

            {/* Dynamic params based on rule_type */}
            {(formData.rule_type === "REQUIRE_PRESENT" ||
              formData.rule_type === "MIN_NUMBER" ||
              formData.rule_type === "REQUIRE_ENRICHMENT_STATUS") && (
              <div className="space-y-2">
                <Label htmlFor="attribute_key">Attribute Key</Label>
                <Input
                  id="attribute_key"
                  value={formData.attribute_key}
                  onChange={(e) => setFormData({ ...formData, attribute_key: e.target.value })}
                  placeholder="e.g., merchant_name"
                />
              </div>
            )}

            {formData.rule_type === "MIN_NUMBER" && (
              <div className="space-y-2">
                <Label htmlFor="min_value">Minimum Value</Label>
                <Input
                  id="min_value"
                  type="number"
                  step="0.01"
                  value={formData.min_value}
                  onChange={(e) => setFormData({ ...formData, min_value: e.target.value })}
                  placeholder="e.g., 0.7"
                />
              </div>
            )}

            {formData.rule_type === "REQUIRE_ENRICHMENT_STATUS" && (
              <div className="space-y-2">
                <Label htmlFor="allowed_values">Allowed Values (comma-separated)</Label>
                <Input
                  id="allowed_values"
                  value={formData.allowed_values}
                  onChange={(e) => setFormData({ ...formData, allowed_values: e.target.value })}
                  placeholder="e.g., complete, verified"
                />
              </div>
            )}

            {formData.rule_type === "REQUIRE_DATE_WINDOW" && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_key">Start Date Key</Label>
                  <Input
                    id="start_key"
                    value={formData.start_key}
                    onChange={(e) => setFormData({ ...formData, start_key: e.target.value })}
                    placeholder="e.g., start_date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end_key">End Date Key</Label>
                  <Input
                    id="end_key"
                    value={formData.end_key}
                    onChange={(e) => setFormData({ ...formData, end_key: e.target.value })}
                    placeholder="e.g., end_date"
                  />
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeDialog} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : isCreating ? "Create Rule" : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
